import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import { useMedia } from './context/MediaContext';
import { useLanguage } from './context/LanguageContext';

const VideoPlayer = () => {
  const { t } = useLanguage();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get('index')) || 0;
  });
  const playerRef = useRef(null);
  const { youtubeVideos } = useMedia();

  const currentVideo = youtubeVideos[currentVideoIndex] || { url: '', loop: false };

  // Send playing state to parent window
  useEffect(() => {
    if (window.opener) {
      window.opener.postMessage({
        type: 'VIDEO_STATE_UPDATE',
        isPlaying,
        currentVideoIndex
      }, '*');

      // Close window when video stops playing while in fullscreen mode
      if (!isPlaying && document.fullscreenElement !== null) {
        document.exitFullscreen().then(() => {
          window.close();
        });
      }
    }
  }, [isPlaying, currentVideoIndex]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen = document.fullscreenElement !== null;
      setIsFullscreen(isInFullscreen);
      setIsPlaying(isInFullscreen);
    };

    const handleMessage = (event) => {
      if (!event.data) return;
      
      if (event.data.type === 'REQUEST_VIDEO_STATE') {
        if (window.opener) {
          window.opener.postMessage({
            type: 'VIDEO_STATE_UPDATE',
            isPlaying,
            currentVideoIndex
          }, '*');
        }
        return;
      }
      if (event.data.type === 'STOP_VIDEO') {
        setIsPlaying(false);
        
        // Wenn das Fenster geschlossen werden soll und im Vollbildmodus ist
        if (document.fullscreenElement) {
          document.exitFullscreen().then(() => {
            window.close();
          }).catch(() => {
            // Fallback: Trotzdem versuchen zu schließen
            window.close();
          });
        } else if (document.webkitFullscreenElement) {
          document.webkitExitFullscreen();
          setTimeout(() => window.close(), 100);
        } else if (document.mozFullScreenElement) {
          document.mozCancelFullScreen();
          setTimeout(() => window.close(), 100);
        } else if (document.msFullscreenElement) {
          document.msExitFullscreen();
          setTimeout(() => window.close(), 100);
        } else {
          // Wenn kein Vollbild aktiv ist, schließe das Fenster direkt
          window.close();
        }
        return;
      }

      if (event.data.type === 'CHANGE_VIDEO') {
        const wasFullscreen = document.fullscreenElement !== null;
        if (wasFullscreen) {
          // Exit fullscreen before changing video
          if (document.exitFullscreen) {
            document.exitFullscreen();
          }
        }
        setCurrentVideoIndex(event.data.index);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('message', handleMessage);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const requestFullscreen = async () => {
    try {
      // Try to use the Screen Details API
      if ('getScreenDetails' in window) {
        console.log('Using Screen Details API');
        const screenDetails = await window.getScreenDetails();
        console.log('Available screens:', screenDetails.screens);
        
        // Get the current screen
        const currentScreen = screenDetails.screens.find(
          screen => screen.isPrimary
        );
        
        // Find a screen that isn't the primary screen
        const targetScreen = screenDetails.screens.find(
          screen => !screen.isPrimary
        );

        console.log('Current screen:', currentScreen);
        console.log('Target screen:', targetScreen);

        if (targetScreen) {
          // Move window to second screen
          window.moveTo(targetScreen.availLeft, targetScreen.availTop);
          
          // Longer delay to ensure window has fully moved
          await new Promise(resolve => setTimeout(resolve, 500));

          // Request fullscreen
          const element = document.documentElement;
          if (element.requestFullscreen) {
            await element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
          } else if (element.msRequestFullscreen) {
            await element.msRequestFullscreen();
          }
          return;
        }
      }
    } catch (error) {
      console.log('Screen Details API failed:', error);
    }

    // Fallback to basic window positioning if Screen Details API is not available
    // or if no second screen was found
    console.log('Using fallback positioning');
    window.moveTo(window.screen.availWidth, 0);
    
    // Longer delay to ensure window has fully moved
    await new Promise(resolve => setTimeout(resolve, 500));

    // Request fullscreen
    const element = document.documentElement;
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      await element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      await element.msRequestFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="relative w-full h-screen">
        <ReactPlayer
          ref={playerRef}
          url={currentVideo.url}
          width="100%"
          height="100%"
          playing={isPlaying}
          volume={volume}
          controls={false}
          loop={currentVideo.loop}
          config={{
            youtube: {
              playerVars: {
                modestbranding: 1,
                rel: 0,
                controls: 0,
                showinfo: 0,
                iv_load_policy: 3,
                disablekb: 1,
                fs: 0,
                playsinline: 1,
                enablejsapi: 1
              }
            }
          }}
          style={{ backgroundColor: 'black' }}
          onError={(e) => {
            console.error('Player Error:', e);
            if (document.fullscreenElement !== null) {
              setIsPlaying(false);
            }
          }}
          onEnded={() => {
            if (!currentVideo.loop) {
              const nextIndex = (currentVideoIndex + 1) % youtubeVideos.length;
              if (youtubeVideos[nextIndex]?.url) {
                setCurrentVideoIndex(nextIndex);
              } else {
                // No more videos to play, stop playing if in fullscreen
                if (document.fullscreenElement !== null) {
                  setIsPlaying(false);
                }
              }
            }
          }}
        />

        <div className="absolute top-4 right-4 flex gap-2">
          {!document.fullscreenElement && (
            <button
              onClick={requestFullscreen}
              className="bg-amber-500 text-black px-6 py-3 rounded-lg hover:bg-amber-600 font-bold text-lg shadow-lg"
              title={t('videoPlayer.fullScreenTooltip')}
            >
              🖥️ {t('videoPlayer.playOnFullScreen')}
            </button>
          )}

        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
